import {
    isEmpty,
    isUndefined,
    isNull,
    forEach,
    values,
    map,
    sortBy,
    isArrayLikeObject,
    isObjectLike,
    takeRight,
    filter,
    find,
    min,
    includes,
    unionBy,
    isString,
} from 'lodash';
import { List } from 'immutable';

import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import * as Constants from 'commons-vehicle/constants/apiDataConversionConstants.js';
import * as dataTypes from 'commons-vehicle/constants/dataTypes.js';
import * as PurchaseMethodsConstants from 'commons-vehicle/PurchaseMethods/constants/purchaseMethods.js';
import { IMAGE_STATUS } from 'commons-vehicle/ModelYear/constants/exteriorColourStatuses.js';
import { MODEL_TAXES } from 'commons-vehicle/PurchaseMethods/constants/dataConversions.js';
import { DEALER_INVENTORY, DEALER_DISCOUNT } from 'commons-vehicle/Quote/constants/modelTrimItemCategories.js';

import ModelYearModel from 'commons-vehicle/ModelYear/models/ModelYearModel.js';
import * as UtilitiesService from 'commons-vehicle/ModelYear/services/DataUtilitiesService/DataUtilitiesService.js';
import { resetModelTrimConfigurationFromModelData } from 'commons-vehicle/ModelYear/services/ModelTrimConfigurationService/ModelTrimConfigurationService.js';
import { resetPurchaseMethodsFromOriginData } from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';
import { resetQuoteItemCategoriesFromOptionsValidation } from 'commons-vehicle/Quote/services/QuoteItemCategoryService/QuoteItemCategoryService.js';
import * as ModelTrimItemsService from 'commons-vehicle/ModelYear/services/ModelTrimItemsService/ModelTrimItemsService.js';
import { resetCabSizeFromOriginData } from 'commons-vehicle/ModelYear/services/CabSizeService/CabSizeService.js';
import { resetBoxSizeFromOriginData } from 'commons-vehicle/ModelYear/services/BoxSizeService/BoxSizeService.js';
import { resetDriveTrainFromOriginData } from 'commons-vehicle/ModelYear/services/DriveTrainService/DriveTrainService.js';
import * as YearService from 'commons-vehicle/ModelYear/services/YearService/YearService.js';
import * as ModelTrimService from 'commons-vehicle/ModelYear/services/ModelTrimService/ModelTrimService.js';
import * as DrivePowerTrainService from 'commons-vehicle/ModelYear/services/DrivePowerTrainService/DrivePowerTrainService.js';
import * as OptionsPackageService from 'commons-vehicle/ModelYear/services/OptionsPackageService/OptionsPackageService.js';
import * as OptionService from 'commons-vehicle/ModelYear/services/OptionService/OptionService.js';
import * as OptionsSelectionResolutionsService from 'commons-vehicle/ModelYear/services/OptionsSelectionResolutionsService/OptionsSelectionResolutionsService.js';
/* eslint no-bitwise: 0 */

function getDefaultModelTrimConfiguration() {
    return {
        year: 0,
        make: '',
        model: '',
        bodyStyleCode: '',
        bodyStyle: '',
        cabSize: {},
        driveTrain: {},
        boxSize: {},
        modelTrim: {},
        configurationId: 0,
        configuration: {},
        exteriorColourStatus: IMAGE_STATUS.NO_IMAGE,
        package: {
            code: '',
            selectionCode: '',
            description: '',
        },
        engine: {
            code: '',
            selectionCode: '',
            fuelTypeCode: '',
            cylinders: 0,
            displacement: 0.0,
            isTurbo: false,
            hp: 0,
            hpRpm: 0,
            torque: 0,
            torqueRpm: 0,
            description: '',
        },
        transmission: {
            code: '',
            selectionCode: '',
            typeCode: '',
            rank: 99,
            label: '',
            type: '',
            speeds: 0,
            isCVT: false,
            description: '',
        },
        priceIds: {
            modelTrimCode: '',
            configurationId: 0,
        },
        leaseIds: {
            modelTrimCode: '',
            configurationId: 0,
        },
        financeIds: {
            modelTrimCode: '',
            configurationId: 0,
        },
    };
}

function setModelTrimFeatures(modelTrimConfigurations, modelTrimFeatures) {
    const newModelTrimFeatures = modelTrimFeatures;

    forEach(modelTrimConfigurations.modelTrimFeatures, (modelTrimFeature, key) => {
        if (isEmpty(modelTrimFeatures[key])) {
            newModelTrimFeatures[key] = {
                trimsBitField: modelTrimConfigurations.trimsBitField,
                feature: modelTrimFeature,
            };
        } else {
            const modelTrimTrimsBitField = newModelTrimFeatures[key].trimsBitField;
            newModelTrimFeatures[key].trimsBitField = modelTrimTrimsBitField | modelTrimConfigurations.trimsBitField;
        }
    });
}

function getModelTrimsConfigurationsFromModelData(modelData, modelTrimsData, modelOptions) {
    const configurations = [];

    if (isEmpty(modelTrimsData) || isEmpty(modelTrimsData[Constants.VIEW_2_1.VEHICLE.MODEL_YEAR.TRIMS])) {
        return configurations;
    }

    const cabSizes = [];
    forEach(modelTrimsData[Constants.VIEW_2_1.VEHICLE.MODEL_YEAR.CABS], (modelCabSize) => {
        cabSizes.push(resetCabSizeFromOriginData(DataOrigins.MODEL_YEAR_POWERS_TRIMS, modelCabSize));
    });

    const boxSizes = [];
    forEach(modelTrimsData[Constants.VIEW_2_1.VEHICLE.MODEL_YEAR.BOXES], (modelBoxSize) => {
        boxSizes.push(resetBoxSizeFromOriginData(DataOrigins.MODEL_YEAR_POWERS_TRIMS, modelBoxSize));
    });

    const driveTrains = [];
    forEach(modelTrimsData[Constants.VIEW_2_1.VEHICLE.MODEL_YEAR.DRIVES], (modelDriveTrain) => {
        driveTrains.push(resetDriveTrainFromOriginData(DataOrigins.MODEL_YEAR_POWERS_TRIMS, modelDriveTrain));
    });

    forEach(modelTrimsData[Constants.VIEW_2_1.VEHICLE.MODEL_YEAR.TRIMS], (trim) => {
        const trimDescription = UtilitiesService.getDescriptionData(trim[Constants.VIEW_2_1.VEHICLE.TRIM.DESCRIPTION]);
        const trimExtensionCode = UtilitiesService.getDescriptionData(
            trim[Constants.VIEW_2_1.VEHICLE.TRIM.EXTENSION_CODE]
        );
        const trimSelectionCode = `${trimDescription}|${trimExtensionCode}`;

        const modelTrim = UtilitiesService.getSelectionObject(
            trim[Constants.VIEW_2_1.VEHICLE.TRIM.BIT_FIELD],
            trimSelectionCode,
            trimSelectionCode,
            trim[Constants.VIEW_2_1.VEHICLE.TRIM.DESCRIPTION]
        );

        modelTrim.extensionCode = UtilitiesService.getDescriptionData(
            trim[Constants.VIEW_2_1.VEHICLE.TRIM.EXTENSION_CODE]
        );
        modelTrim.extensionDescription = UtilitiesService.getDescriptionData(
            trim[Constants.VIEW_2_1.VEHICLE.TRIM.EXTENSION_DESCRIPTION]
        );

        modelTrim.hasOptions = trim[Constants.VIEW_2_1.VEHICLE.TRIM.HAS_OPTIONS];

        const modelTrimFeatures = {};
        let allConfigTrimsBitField = 0;
        if (!isEmpty(trim[Constants.VIEW_2_1.VEHICLE.TRIM.CONFIGURATIONS])) {
            const modelTrimConfigs = [];
            forEach(trim[Constants.VIEW_2_1.VEHICLE.TRIM.CONFIGURATIONS], (modelTrimConfiguration) => {
                if (modelTrimConfiguration.length === MODEL_TAXES.TAXES_FACTOR - 1) {
                    modelTrimConfiguration.push(IMAGE_STATUS.NO_IMAGE);
                }
                modelTrimConfiguration.push(modelData.taxesFactor);
                modelTrimConfiguration.push(modelData.taxCodes);

                const conf = resetModelTrimConfigurationFromModelData(
                    modelTrimConfiguration,
                    modelOptions,
                    modelTrim,
                    driveTrains,
                    cabSizes,
                    boxSizes
                );
                setModelTrimFeatures(conf, modelTrimFeatures);
                const tempTrimsBitField = allConfigTrimsBitField;
                allConfigTrimsBitField = tempTrimsBitField | conf.trimsBitField;

                modelTrimConfigs.push(conf);
                configurations.push(conf);
            });
        }
    });

    return configurations;
}

function getModelTrimsFromModelData(dataOrigin, modelTrimsData) {
    const vehicle = {
        year: 0,
        make: '',
        model: '',
        bodyStyleCode: '',
        bodyStyle: '',
        vehicles: [],
    };

    switch (dataOrigin) {
        case DataOrigins.MODEL_YEAR_POWERS_TRIMS: {
            vehicle.make = modelTrimsData.make;
            vehicle.model = modelTrimsData.model;
            vehicle.bodyStyleCode = modelTrimsData.bodyStyleCode;
            vehicle.bodyStyle = modelTrimsData.bodyStyle[Constants.DESCRIPTION_LANGUAGE_INDEX];

            vehicle.year = modelTrimsData.years[0][Constants.VIEW_2_1.VEHICLE.MODEL_YEAR.YEAR];

            const modelOptions = ModelTrimItemsService.getModelYearFeaturesAndOptions(modelTrimsData.years[0]);

            const modelTrimsConfigurations = getModelTrimsConfigurationsFromModelData(
                modelTrimsData,
                modelTrimsData.years[0],
                modelOptions
            );

            vehicle.vehicles = map(modelTrimsConfigurations, (modelTrimConfiguration) => {
                const configurationVehicle = getDefaultModelTrimConfiguration();
                configurationVehicle.year = vehicle.year;
                configurationVehicle.make = vehicle.make;
                configurationVehicle.model = vehicle.model;
                configurationVehicle.bodyStyleCode = vehicle.bodyStyleCode;
                configurationVehicle.bodyStyle = vehicle.bodyStyleCode;

                configurationVehicle.cabSize = modelTrimConfiguration.cabSize;
                configurationVehicle.boxSize = modelTrimConfiguration.boxSize;
                configurationVehicle.driveTrain = modelTrimConfiguration.driveTrain;

                configurationVehicle.modelTrimCode = modelTrimConfiguration.modelTrimCode;
                configurationVehicle.modelTrim = modelTrimConfiguration.modelTrim;

                configurationVehicle.configurationId = modelTrimConfiguration.id;
                configurationVehicle.configuration = modelTrimConfiguration;
                configurationVehicle.package = modelTrimConfiguration.package;
                configurationVehicle.engine = modelTrimConfiguration.engine;
                configurationVehicle.transmission = modelTrimConfiguration.transmission;

                return {
                    vehicle: configurationVehicle,
                    purchaseMethods: modelTrimConfiguration.purchaseMethods,
                };
            });
            break;
        }
        default:
            break;
    }

    return vehicle;
}

function getModelTrimFromModelData(dataOrigin, modelTrimData) {
    const modelTrim = getDefaultModelTrimConfiguration();

    switch (dataOrigin) {
        case DataOrigins.MODELS_COLLECTION:
            modelTrim.make = modelTrimData.make;
            modelTrim.model = modelTrimData.model;
            modelTrim.bodyStyleCode = modelTrimData.bodyStyleCode;
            modelTrim.bodyStyle = modelTrimData.bodyStyle[Constants.DESCRIPTION_LANGUAGE_INDEX];

            if (modelTrimData[Constants.VIEW_1.VEHICLE.HIGH_PRICE]) {
                const highPriceObject =
                    modelTrimData[Constants.VIEW_1.VEHICLE.HIGH_PRICE][Constants.VIEW_1.VEHICLE.HIGH_PRICE_INDEX];
                modelTrim.exteriorColours = {
                    photo1:
                        highPriceObject[Constants.VIEW_1.VEHICLE.URL_EXTERIOR_COLOUR][
                            Constants.VIEW_1.COLOUR_IMAGE.PHOTO_1
                        ],
                    photo2:
                        highPriceObject[Constants.VIEW_1.VEHICLE.URL_EXTERIOR_COLOUR][
                            Constants.VIEW_1.COLOUR_IMAGE.PHOTO_2
                        ],
                    photo3:
                        highPriceObject[Constants.VIEW_1.VEHICLE.URL_EXTERIOR_COLOUR][
                            Constants.VIEW_1.COLOUR_IMAGE.PHOTO_3
                        ],
                };

                if (!isUndefined(highPriceObject[Constants.VIEW_1.VEHICLE.EXTERIOR_COLOUR_STATUS])) {
                    modelTrim.exteriorColourStatus = highPriceObject[Constants.VIEW_1.VEHICLE.EXTERIOR_COLOUR_STATUS];
                } else if (modelTrim.exteriorColours.photo1) {
                    modelTrim.exteriorColourStatus = IMAGE_STATUS.FULL_COLORIZED;
                }
            }

            if (modelTrimData[Constants.VIEW_1.VEHICLE.REBATE]) {
                const rebateObject =
                    modelTrimData[Constants.VIEW_1.VEHICLE.REBATE][Constants.VIEW_1.VEHICLE.REBATE_INDEX];
                modelTrim.rebate = rebateObject[Constants.VIEW_1.VEHICLE.REBATE_RETAIL];
            }

            if (modelTrimData[Constants.VIEW_1.VEHICLE.HIGH_REBATE]) {
                const highRebateObject =
                    modelTrimData[Constants.VIEW_1.VEHICLE.HIGH_REBATE][Constants.VIEW_1.VEHICLE.HIGH_REBATE_INDEX];
                const highRebateTaxes = highRebateObject[Constants.VIEW_1.VEHICLE.HIGH_REBATE_REBATES_TAXES] || 0;
                modelTrim.highRebate = highRebateObject[Constants.VIEW_1.VEHICLE.HIGH_REBATE_RETAIL] + highRebateTaxes;
                modelTrim.highRebatePurchaseMethod =
                    highRebateObject[Constants.VIEW_1.VEHICLE.HIGH_REBATE_PURCHASE_METHOD];
            }

            if (modelTrimData[Constants.VIEW_1.VEHICLE.PRICE]) {
                const priceObject = modelTrimData[Constants.VIEW_1.VEHICLE.PRICE][Constants.VIEW_1.VEHICLE.PRICE_INDEX];
                if (priceObject[Constants.VIEW_1.VEHICLE.PRICE_DISCLAIMER]) {
                    modelTrim.priceModelDisclaimer =
                        priceObject[Constants.VIEW_1.VEHICLE.PRICE_DISCLAIMER][
                            Constants.VIEW_1.PRICE_DISCLAIMER.MODEL_SHORT
                        ];
                    modelTrim.priceExclusionsDisclaimer =
                        priceObject[Constants.VIEW_1.VEHICLE.PRICE_DISCLAIMER][
                            Constants.VIEW_1.PRICE_DISCLAIMER.EXCLUSIONS
                        ];
                }

                if (
                    priceObject[Constants.VIEW_1.VEHICLE.PRICE_MODEL_TRIM_CODE] &&
                    priceObject[Constants.VIEW_1.VEHICLE.PRICE_MODEL_TRIM_CODE]
                ) {
                    modelTrim.priceIds.year = priceObject[Constants.VIEW_1.VEHICLE.PRICE_YEAR];
                    modelTrim.priceIds.modelTrimCode = priceObject[Constants.VIEW_1.VEHICLE.PRICE_MODEL_TRIM_CODE];
                    modelTrim.priceIds.configurationId = priceObject[Constants.VIEW_1.VEHICLE.PRICE_CONFIGURATION_ID];
                }
            }

            modelTrim.leaseModelDisclaimer = '';
            if (modelTrimData[Constants.VIEW_1.VEHICLE.LEASE]) {
                const leaseObject = modelTrimData[Constants.VIEW_1.VEHICLE.LEASE][Constants.VIEW_1.VEHICLE.LEASE_INDEX];
                if (leaseObject[Constants.VIEW_1.VEHICLE.LEASE_DISCLAIMER]) {
                    modelTrim.leaseModelDisclaimer =
                        leaseObject[Constants.VIEW_1.VEHICLE.LEASE_DISCLAIMER][
                            Constants.VIEW_1.FINANCE_DISCLAIMER.MODEL_SHORT
                        ];
                }

                if (
                    leaseObject[Constants.VIEW_1.VEHICLE.LEASE_MODEL_TRIM_CODE] &&
                    leaseObject[Constants.VIEW_1.VEHICLE.LEASE_CONFIGURATION_ID]
                ) {
                    modelTrim.leaseIds.year = leaseObject[Constants.VIEW_1.VEHICLE.LEASE_YEAR];
                    modelTrim.leaseIds.modelTrimCode = leaseObject[Constants.VIEW_1.VEHICLE.LEASE_MODEL_TRIM_CODE];
                    modelTrim.leaseIds.configurationId = leaseObject[Constants.VIEW_1.VEHICLE.LEASE_CONFIGURATION_ID];
                }
            }

            modelTrim.financeModelDisclaimer = '';
            if (modelTrimData[Constants.VIEW_1.VEHICLE.FINANCE]) {
                const financeObject =
                    modelTrimData[Constants.VIEW_1.VEHICLE.FINANCE][Constants.VIEW_1.VEHICLE.FINANCE_INDEX];
                if (financeObject[Constants.VIEW_1.VEHICLE.FINANCE_DISCLAIMER]) {
                    modelTrim.financeModelDisclaimer =
                        financeObject[Constants.VIEW_1.VEHICLE.FINANCE_DISCLAIMER][
                            Constants.VIEW_1.FINANCE_DISCLAIMER.MODEL_SHORT
                        ];
                }

                if (
                    financeObject[Constants.VIEW_1.VEHICLE.FINANCE_MODEL_TRIM_CODE] &&
                    financeObject[Constants.VIEW_1.VEHICLE.FINANCE_CONFIGURATION_ID]
                ) {
                    modelTrim.financeIds.year = financeObject[Constants.VIEW_1.VEHICLE.FINANCE_YEAR];
                    modelTrim.financeIds.modelTrimCode =
                        financeObject[Constants.VIEW_1.VEHICLE.FINANCE_MODEL_TRIM_CODE];
                    modelTrim.financeIds.configurationId =
                        financeObject[Constants.VIEW_1.VEHICLE.FINANCE_CONFIGURATION_ID];
                }
            }

            modelTrim.makeVisible = true;
            modelTrim.bodyStyleVisible = true;
            modelTrim.modelVisible = true;
            modelTrim.visible = true;
            break;

        default:
            break;
    }

    return modelTrim;
}

function resetModelComparisonFeatures(model) {
    let prevTrimID = '';
    let prevTrim = '';
    let prevTrimExtension = '';
    let prevTrimFeatures = {};

    return map(model.modelTrims, (modelTrim) => {
        const newModelTrim = ModelTrimService.resetModelTrimComparisonFeatures(
            prevTrimID,
            prevTrim,
            prevTrimExtension,
            prevTrimFeatures,
            modelTrim
        );
        prevTrimID = modelTrim.selectionCode;
        prevTrim = modelTrim.description;
        prevTrimExtension = modelTrim.trimExtensionDescription;
        prevTrimFeatures = modelTrim.features;

        return newModelTrim;
    });
}

function getModelPowersAndTrimsFromModelData(modelPowersAndTrimsData, disclaimers) {
    const modelYear = {
        isSingleConfiguration: true,
        hasOptions: false,
        cabSizes: {},
        boxSizes: {},
        modelTrims: {},
        drivePowerTrains: {},
        optionsPackages: {},
    };

    if (isEmpty(modelPowersAndTrimsData)) {
        return modelYear;
    }

    const modelTrimDrivePowerConfigurationCount = {};

    forEach(modelPowersAndTrimsData, (modelPowerAndTrimData) => {
        const modelPowerAndTrim = modelPowerAndTrimData.vehicle;

        if (modelPowerAndTrim.configuration.modelTrim.hasOptions) {
            modelYear.hasOptions = true;
        }

        const drivePowerTrainSelectionCode =
            `${modelPowerAndTrim.driveTrain.selectionCode}|` +
            `${modelPowerAndTrim.engine.getSelectionCode()}|` +
            `${modelPowerAndTrim.transmission.getSelectionCode()}`;

        const cabSizeSelectionCode = isNull(modelPowerAndTrim.cabSize) ? '' : modelPowerAndTrim.cabSize.selectionCode;
        const boxSizeSelectionCode = isNull(modelPowerAndTrim.boxSize) ? '' : modelPowerAndTrim.boxSize.selectionCode;
        const modelTrimSelectionCode = modelPowerAndTrim.modelTrim.selectionCode;
        const packageSelectionCode =
            `${cabSizeSelectionCode}${modelPowerAndTrim.package.selectionCode}|` +
            `${modelPowerAndTrim.configurationId}`;
        const modelTrimConfigurationId = modelPowerAndTrim.configuration.selectionCode;

        if (cabSizeSelectionCode && isEmpty(modelYear.cabSizes[cabSizeSelectionCode])) {
            modelYear.cabSizes[cabSizeSelectionCode] = modelPowerAndTrim.cabSize;
        }

        if (boxSizeSelectionCode && isEmpty(modelYear.boxSizes[boxSizeSelectionCode])) {
            modelYear.boxSizes[boxSizeSelectionCode] = modelPowerAndTrim.boxSize;
        }

        if (isEmpty(modelYear.modelTrims[modelTrimSelectionCode])) {
            modelTrimDrivePowerConfigurationCount[modelTrimSelectionCode] = {};
            modelYear.modelTrims[modelTrimSelectionCode] = ModelTrimService.resetModelTrimFromOriginData(
                DataOrigins.MODEL_YEAR_POWERS_TRIMS,
                modelPowerAndTrim
            );
        } else if (modelYear.modelTrims[modelTrimSelectionCode].rank > modelPowerAndTrim.configuration.modelTrimRank) {
            modelYear.modelTrims[modelTrimSelectionCode] = modelYear.modelTrims[modelTrimSelectionCode].set(
                'rank',
                modelPowerAndTrim.configuration.modelTrimRank
            );
        }

        if (isEmpty(modelYear.drivePowerTrains[drivePowerTrainSelectionCode])) {
            modelYear.drivePowerTrains[
                drivePowerTrainSelectionCode
            ] = DrivePowerTrainService.resetDrivePowerTrainFromOriginData(
                DataOrigins.MODEL_YEAR_POWERS_TRIMS,
                modelPowerAndTrim
            );
        }

        if (isUndefined(modelTrimDrivePowerConfigurationCount[modelTrimSelectionCode][drivePowerTrainSelectionCode])) {
            modelTrimDrivePowerConfigurationCount[modelTrimSelectionCode][drivePowerTrainSelectionCode] = 0;
        } else {
            modelYear.isSingleConfiguration = false;
        }
        modelTrimDrivePowerConfigurationCount[modelTrimSelectionCode][drivePowerTrainSelectionCode] += 1;

        if (isEmpty(modelYear.optionsPackages[packageSelectionCode])) {
            modelYear.optionsPackages[packageSelectionCode] = OptionsPackageService.resetOptionsPackageFromOriginData(
                DataOrigins.MODEL_YEAR_POWERS_TRIMS,
                modelPowerAndTrim,
                disclaimers
            );
        }

        if (
            isEmpty(modelYear.drivePowerTrains[drivePowerTrainSelectionCode].configurationIds[modelTrimConfigurationId])
        ) {
            modelYear.drivePowerTrains[
                drivePowerTrainSelectionCode
            ] = DrivePowerTrainService.addDrivePowerTrainConfigurationId(
                modelYear.drivePowerTrains[drivePowerTrainSelectionCode],
                modelTrimConfigurationId,
                modelPowerAndTrim.configuration
            );
        }

        if (isEmpty(modelYear.modelTrims[modelTrimSelectionCode].configurationIds[modelTrimConfigurationId])) {
            modelYear.modelTrims[modelTrimSelectionCode] = ModelTrimService.addModelTrimConfigurationId(
                modelYear.modelTrims[modelTrimSelectionCode],
                modelTrimConfigurationId,
                modelPowerAndTrim.configuration
            );
        }
    });

    modelYear.cabSizes = sortBy(modelYear.cabSizes, (cabSize) => {
        return cabSize.rank;
    });

    modelYear.boxSizes = sortBy(modelYear.boxSizes, (boxSize) => {
        return boxSize.rank;
    });

    modelYear.modelTrims = sortBy(modelYear.modelTrims, (modelTrim) => {
        return modelTrim.rank;
    });

    modelYear.drivePowerTrains = sortBy(modelYear.drivePowerTrains, (drivePowerTrain) => {
        return drivePowerTrain.rank;
    });

    modelYear.optionsPackages = sortBy(modelYear.optionsPackages, (optionsPackage) => {
        return optionsPackage.rank;
    });

    modelYear.modelTrims = resetModelComparisonFeatures(modelYear);

    return modelYear;
}

function resetModelYearFromModelData(modelModelYearData, locale, disclaimers) {
    const { vehicle } = modelModelYearData[0];
    const powersAndTrims = getModelPowersAndTrimsFromModelData(modelModelYearData, disclaimers);

    return new ModelYearModel({
        lang: locale,
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        bodyStyleCode: vehicle.bodyStyleCode,
        isSingleConfiguration: powersAndTrims.isSingleConfiguration,
        hasOptions: powersAndTrims.hasOptions,
        cabSizes: values(powersAndTrims.cabSizes),
        boxSizes: values(powersAndTrims.boxSizes),
        modelTrims: List().concat(values(powersAndTrims.modelTrims).map((modelTrim) => modelTrim)),
        drivePowerTrains: List().concat(
            values(powersAndTrims.drivePowerTrains).map((drivePowerTrain) => drivePowerTrain)
        ),
        optionsPackages: List().concat(values(powersAndTrims.optionsPackages).map((pack) => pack)),
    });
}

function getMergedModelTrimTrimsBitField(modelTrim) {
    let modelTrimTrimsBitField = {
        trimsBitField: modelTrim.trimsBitField,
        trimsBitFields: modelTrim.trimsBitFields,
    };

    if (modelTrim.cabSizeTrimsBitField || !isEmpty(modelTrim.cabSizeTrimsBitFields)) {
        modelTrimTrimsBitField = UtilitiesService.mergeModelTrimsBitField(
            modelTrimTrimsBitField,
            {
                trimsBitField: modelTrim.cabSizeTrimsBitField,
                trimsBitFields: modelTrim.cabSizeTrimsBitFields,
            },
            false
        );
    }
    if (modelTrim.boxSizeTrimsBitField || !isEmpty(modelTrim.boxSizeTrimsBitFields)) {
        modelTrimTrimsBitField = UtilitiesService.mergeModelTrimsBitField(
            modelTrimTrimsBitField,
            {
                trimsBitField: modelTrim.boxSizeTrimsBitField,
                trimsBitFields: modelTrim.boxSizeTrimsBitFields,
            },
            false
        );
    }
    return modelTrimTrimsBitField;
}

function resetModelYearItemVisibility(modelYearItem, trimsBitField) {
    return modelYearItem
        .set('visible', UtilitiesService.intersectModelTrimsBitFieldValue(modelYearItem, trimsBitField) > 0)
        .set('selected', false);
}

function resetModelYearOptionPackagesVisibility(
    modelYearOptionsPackage,
    trimsBitField,
    engineSelectionCode,
    transmissionSelectionCode
) {
    return modelYearOptionsPackage
        .set(
            'visible',
            UtilitiesService.intersectModelTrimsBitFieldValue(modelYearOptionsPackage, trimsBitField) > 0 &&
                modelYearOptionsPackage.configuration.engine.selectionCode === engineSelectionCode &&
                modelYearOptionsPackage.configuration.transmission.selectionCode === transmissionSelectionCode
        )
        .set('selected', false);
}

function resetModelYearItemAvailability(modelYearItem, trimsBitField) {
    return modelYearItem
        .set('enabled', UtilitiesService.intersectModelTrimsBitFieldValue(modelYearItem, trimsBitField) > 0)
        .set('selected', false);
}

function resetModelTrimsAvailabilityFromBoxSize(modelTrim, boxSizeTrimsBitField) {
    const mergedModelTrimAndCab = UtilitiesService.mergeModelTrimsBitField(
        modelTrim,
        {
            trimsBitField: modelTrim.cabSizeTrimsBitField,
            trimsBitFields: modelTrim.cabSizeTrimsBitFields,
        },
        false
    );

    return modelTrim
        .set('boxSizeTrimsBitField', boxSizeTrimsBitField.trimsBitField)
        .set('boxSizeTrimsBitFields', boxSizeTrimsBitField.trimsBitFields)
        .set(
            'enabled',
            UtilitiesService.intersectModelTrimsBitFieldValue(mergedModelTrimAndCab, boxSizeTrimsBitField) > 0
        )
        .set('selected', false);
}

function selectModelYearItemFromCode(modelYearItem, selectionCode) {
    return modelYearItem.set('selected', modelYearItem.selectionCode === selectionCode);
}

export function resetModelYearFromOriginData(dataOrigin, originModelYearData, locale, disclaimers) {
    if (isEmpty(originModelYearData) || !isObjectLike(originModelYearData)) {
        return new ModelYearModel();
    }
    let vehicles = [];

    switch (dataOrigin) {
        case DataOrigins.MODELS_COLLECTION:
            vehicles = map(originModelYearData, (modelData) => {
                const vehicle = {};
                vehicle.vehicle = getModelTrimFromModelData(dataOrigin, modelData);
                const purchaseMethods = resetPurchaseMethodsFromOriginData(dataOrigin, modelData);
                vehicle.purchaseMethods = disclaimers
                    ? purchaseMethods.set('disclaimers', disclaimers)
                    : purchaseMethods;

                return vehicle;
            });

            return vehicles;

        case DataOrigins.MODEL_YEARS: {
            const convertedVehicle = {
                years: [],
            };

            forEach(originModelYearData, (modelData) => {
                forEach(modelData, (modelYearData, key) => {
                    convertedVehicle.years.push(
                        YearService.resetYearFromOriginData(
                            DataOrigins.MODELS_COLLECTION,
                            modelYearData,
                            parseInt(key, 10),
                            disclaimers
                        )
                    );
                });
            });

            if (convertedVehicle.years.length > 2) {
                convertedVehicle.years = takeRight(
                    sortBy(convertedVehicle.years, (y) => {
                        return y.year;
                    }),
                    2
                );
            }

            convertedVehicle.years = List(convertedVehicle.years);
            return convertedVehicle;
        }
        case DataOrigins.MODEL_YEAR_POWERS_TRIMS:
            forEach(originModelYearData, (modelYearData) => {
                const modelTrims = getModelTrimsFromModelData(dataOrigin, modelYearData);

                forEach(modelTrims.vehicles, (vehicle) => {
                    vehicles.push({
                        vehicle: vehicle.vehicle,
                        purchaseMethods: vehicle.purchaseMethods,
                    });
                });
            });

            return {
                modelYear: resetModelYearFromModelData(vehicles, locale, disclaimers),
                purchaseMethods: disclaimers
                    ? vehicles[0].purchaseMethods.set('disclaimers', disclaimers)
                    : vehicles[0].purchaseMethods,
            };

        default:
            return new ModelYearModel();
    }
}

function resetSelectedExteriorColour(modelYear) {
    if (!modelYear.getSelectedOptionsPackageExteriorColour()) {
        const exteriorColor = modelYear.getSelectedOptionsPackageBestAvailableExteriorColour();
        if (!exteriorColor) {
            return modelYear;
        }
        const exteriorColourSelectionCode = exteriorColor.colourCode;
        return modelYear.set(
            'optionsPackages',
            modelYear.optionsPackages.map((optionsPackage) =>
                OptionsPackageService.resetOptionsPackageExteriorColours(optionsPackage, exteriorColourSelectionCode)
            )
        );
    }

    return modelYear;
}

function resetSelectedOptionsPackage(modelYear) {
    const availableOptionsPackages = modelYear.optionsPackages.filter((optionsPackage) => optionsPackage.visible);

    if (availableOptionsPackages.size === 0) {
        return modelYear;
    }

    const optionsPackageSelectionCode = availableOptionsPackages.minBy((optionsPackage) => optionsPackage.rank)
        .selectionCode;
    const newModelYear = modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            selectModelYearItemFromCode(optionsPackage, optionsPackageSelectionCode)
        )
    );

    return resetSelectedExteriorColour(newModelYear);
}

function resetDrivePowerTrainWithOptionsPackage(
    modelYear,
    drivePowerTrain,
    drivePowerTrainSelectionCode,
    modelTrimTrimsBitField
) {
    const drivePowerTrainTrimsBitField = UtilitiesService.mergeModelTrimsBitField(
        drivePowerTrain,
        modelTrimTrimsBitField,
        false
    );
    return selectModelYearItemFromCode(drivePowerTrain, drivePowerTrainSelectionCode).set(
        'bestOptionsPackageCode',
        modelYear.getBestAvailableOptionsPackageFromTrimsBitField(
            drivePowerTrainTrimsBitField,
            drivePowerTrain.engine.selectionCode,
            drivePowerTrain.transmission.selectionCode
        )
    );
}

export function setSelectedDrivePowerTrain(modelYear, selectedDrivePowerTrain) {
    const selectedModelTrim = modelYear.getSelectedModelTrim();
    if (!selectedModelTrim) {
        return modelYear;
    }
    const modelTrimTrimsBitField = getMergedModelTrimTrimsBitField(selectedModelTrim);

    const drivePowerTrainSelectionCode = selectedDrivePowerTrain.selectionCode;
    const optionsPackageTrimsBitField = UtilitiesService.mergeModelTrimsBitField(
        modelTrimTrimsBitField,
        selectedDrivePowerTrain,
        false
    );

    const engineSelectionCode = selectedDrivePowerTrain.engine.selectionCode;
    const transmissionSelectionCode = selectedDrivePowerTrain.transmission.selectionCode;
    const newModelYear = modelYear
        .set(
            'drivePowerTrains',
            modelYear.drivePowerTrains.map((drivePowerTrain) =>
                selectModelYearItemFromCode(drivePowerTrain, drivePowerTrainSelectionCode)
            )
        )
        .set(
            'optionsPackages',
            modelYear.optionsPackages.map((optionsPackage) =>
                resetModelYearOptionPackagesVisibility(
                    optionsPackage,
                    optionsPackageTrimsBitField,
                    engineSelectionCode,
                    transmissionSelectionCode
                )
            )
        );

    return resetSelectedOptionsPackage(newModelYear);
}

function resetSelectedDrivePowerTrain(modelYear, modelTrimTrimsBitField) {
    const availableDrivePowerTrains = modelYear.drivePowerTrains.filter((drivePowerTrain) => drivePowerTrain.visible);

    if (availableDrivePowerTrains.size === 0) {
        return modelYear;
    }

    const drivePowerTrainSelectionCode = availableDrivePowerTrains.minBy((drivePowerTrain) => drivePowerTrain.rank)
        .selectionCode;

    const newModelYear = modelYear.set(
        'drivePowerTrains',
        modelYear.drivePowerTrains.map((drivePowerTrain) =>
            resetDrivePowerTrainWithOptionsPackage(
                modelYear,
                drivePowerTrain,
                drivePowerTrainSelectionCode,
                modelTrimTrimsBitField
            )
        )
    );

    const selectedDrivePowerTrain = newModelYear.getSelectedDrivePowerTrain();
    if (selectedDrivePowerTrain) {
        return setSelectedDrivePowerTrain(newModelYear, selectedDrivePowerTrain);
    }
    return resetSelectedOptionsPackage(newModelYear);
}

export function setSelectedModelTrim(modelYear, selectedModelTrim) {
    const modelTrimSelectionCode = selectedModelTrim.selectionCode;
    const modelTrimTrimsBitField = getMergedModelTrimTrimsBitField(selectedModelTrim);

    const newModelYear = modelYear
        .set(
            'modelTrims',
            modelYear.modelTrims.map((modelTrim) => selectModelYearItemFromCode(modelTrim, modelTrimSelectionCode))
        )
        .set(
            'drivePowerTrains',
            modelYear.drivePowerTrains.map((drivePowerTrain) =>
                resetModelYearItemVisibility(drivePowerTrain, modelTrimTrimsBitField)
            )
        )
        .set(
            'optionsPackages',
            modelYear.optionsPackages.map((optionsPackage) =>
                resetModelYearItemVisibility(optionsPackage, modelTrimTrimsBitField)
            )
        );

    return resetSelectedDrivePowerTrain(newModelYear, modelTrimTrimsBitField);
}

function resetSelectedModelTrim(modelYear) {
    const availableModelTrims = modelYear.modelTrims.filter((modelTrim) => modelTrim.enabled);

    if (availableModelTrims.size === 0) {
        return modelYear;
    }

    const selectedModelTrim = availableModelTrims.minBy((modelTrim) => modelTrim.rank);
    return setSelectedModelTrim(modelYear, selectedModelTrim);
}

export function setSelectedBoxSize(modelYear, selectedBoxSize) {
    if (!selectedBoxSize.enabled) {
        return modelYear;
    }

    const boxSizeSelectionCode = selectedBoxSize.selectionCode;
    const boxSizeTrimsBitField = {
        trimsBitField: selectedBoxSize.trimsBitField,
        trimsBitFields: selectedBoxSize.trimsBitFields,
    };

    const newModelYear = modelYear
        .set(
            'boxSizes',
            modelYear.boxSizes.map((boxSize) => selectModelYearItemFromCode(boxSize, boxSizeSelectionCode))
        )
        .set(
            'modelTrims',
            modelYear.modelTrims.map((modelTrim) =>
                resetModelTrimsAvailabilityFromBoxSize(modelTrim, boxSizeTrimsBitField)
            )
        );

    return resetSelectedModelTrim(newModelYear);
}

function resetSelectedBoxSize(modelYear) {
    const availableBoxSizes = modelYear.boxSizes.filter((boxSize) => boxSize.enabled);

    if (availableBoxSizes.size === 0) {
        return modelYear;
    }

    const selectedBoxSize = min(availableBoxSizes, (boxSize) => {
        return boxSize.rank;
    });
    return setSelectedBoxSize(modelYear, selectedBoxSize);
}

export function setSelectedCabSize(modelYear, selectedCabSize) {
    const cabSizeSelectionCode = selectedCabSize.selectionCode;
    const cabSizeTrimsBitField = {
        trimsBitField: selectedCabSize.trimsBitField,
        trimsBitFields: selectedCabSize.trimsBitFields,
    };

    const newModelYear = modelYear
        .set(
            'cabSizes',
            modelYear.cabSizes.map((cabSize) => selectModelYearItemFromCode(cabSize, cabSizeSelectionCode))
        )
        .set(
            'boxSizes',
            modelYear.boxSizes.map((boxSize) => resetModelYearItemAvailability(boxSize, cabSizeTrimsBitField))
        )
        .set(
            'modelTrims',
            modelYear.modelTrims.map((modelTrim) =>
                modelTrim
                    .set('cabSizeTrimsBitField', selectedCabSize.trimsBitField)
                    .set('cabSizeTrimsBitFields', selectedCabSize.trimsBitFields)
            )
        );

    return resetSelectedBoxSize(newModelYear, selectedCabSize);
}

export function setSelectedOptionsPackage(modelYear, selectedOptionsPackage, forceSelect) {
    if (!forceSelect && (!modelYear.getSelectedModelTrim() || !modelYear.getSelectedDrivePowerTrain())) {
        return modelYear;
    }

    const optionsPackageSelectionCode = selectedOptionsPackage.selectionCode;
    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            selectModelYearItemFromCode(optionsPackage, optionsPackageSelectionCode)
        )
    );
}

export function setSelectedExteriorColour(modelYear, exteriorColour) {
    const exteriorColourSelectionCode = exteriorColour.colourCode;

    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            OptionsPackageService.resetOptionsPackageExteriorColours(optionsPackage, exteriorColourSelectionCode)
        )
    );
}

export function setSelectedInteriorColour(modelYear, interiorColour) {
    const interiorColourSelectionCode = isNull(interiorColour) ? '' : interiorColour.colourCode;

    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            OptionsPackageService.resetOptionsPackageInteriorColours(optionsPackage, interiorColourSelectionCode)
        )
    );
}

function resetYear(year, modelYear) {
    if (year.year === modelYear.year) {
        return year
            .set('selected', true)
            .set('isSingleConfiguration', modelYear.isSingleConfiguration)
            .set('hasOptions', modelYear.hasOptions)
            .set('modelTrims', modelYear.modelTrims)
            .set('drivePowerTrains', modelYear.drivePowerTrains)
            .set('optionsPackages', modelYear.optionsPackages);
    }

    return year.set('selected', false);
}

export function resetModelYears(modelYear, modelYears, selectedYear) {
    const lastYear = modelYears.last();
    const newModelYear = modelYear
        .set('year', selectedYear || lastYear.year)
        .set('make', lastYear.make)
        .set('model', lastYear.model)
        .set('bodyStyleCode', lastYear.bodyStyleCode);

    return newModelYear.set('years', modelYears.map((year) => resetYear(year, newModelYear)));
}

export function resetModelYear(modelYear, year) {
    if (year) {
        const newModelYear = modelYear
            .set('modelTrims', year.modelTrims)
            .set('drivePowerTrains', year.drivePowerTrains)
            .set('optionsPackages', year.optionsPackages);

        return setSelectedModelTrim(newModelYear, newModelYear.modelTrims.first());
    }

    return modelYear
        .set('modelTrims', List([]))
        .set('drivePowerTrains', List([]))
        .set('optionsPackages', List([]));
}

export function resetConfigurationToken(modelYear, configurationToken) {
    if (!isString(configurationToken)) {
        return modelYear;
    }

    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map(
            (optionsPackage) =>
                optionsPackage.selected
                    ? optionsPackage.setIn(['configuration', 'configurationToken'], configurationToken)
                    : optionsPackage
        )
    );
}

export function resetOptionsAvailability(modelYear, options) {
    if (!isArrayLikeObject(options)) {
        return modelYear;
    }

    const availableOptions = filter(options, (option) => {
        return (option.flag & dataTypes.ITEM_FLAG.AVAILABLE) > 0 && option.supplier === dataTypes.SUPPLIER_OPTION;
    });
    const availableOptionsCodes = map(availableOptions, (option) => {
        return option.code;
    });

    const selectedOptions = filter(options, (option) => {
        return (
            (option.flag & dataTypes.ITEM_FLAG.SELECTED) > 0 &&
            (option.flag & dataTypes.ITEM_FLAG.CONFIGURATION_SELECTED) === 0
        );
    }).map((option) => {
        return OptionService.resetOptionFromOptionData(option);
    });

    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            OptionsPackageService.resetOptionsPackageOptionsAvailability(
                optionsPackage,
                availableOptionsCodes,
                selectedOptions
            )
        )
    );
}

export function resetOptionsSelectionResolutions(modelYear, resolutions) {
    if (!isObjectLike(resolutions)) {
        return modelYear;
    }

    const configuration = modelYear.getSelectedOptionsPackageConfiguration();
    const optionsSelectionResolutions = OptionsSelectionResolutionsService.resetResolutionOptionsFromResolutionsData(
        resolutions.resolutions,
        configuration,
        resolutions.optionSelectionCode
    );

    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            OptionsPackageService.resetOptionsPackageOptionsSelectionResolutions(
                optionsPackage,
                optionsSelectionResolutions
            )
        )
    );
}

export function setAvailableOptions(modelYear, options) {
    if (!isArrayLikeObject(options)) {
        return modelYear;
    }

    const configurationOptions = filter(options, (option) => {
        return (option.flag & dataTypes.ITEM_FLAG.SELECTED) > 0;
    }).map((option) => {
        return OptionService.resetOptionFromProtectionItem(option);
    });
    const configurationOptionsCodes = map(configurationOptions, (configurationOption) => {
        return configurationOption.selectionCode;
    });

    const configurationAvailableOptions = filter(options, (option) => {
        return (option.flag & dataTypes.ITEM_FLAG.CONFIGURATION_AVAILABLE) > 0;
    });

    const availableOptions = filter(configurationAvailableOptions, (option) => {
        return (
            (option.flag & (dataTypes.ITEM_FLAG.CONFIGURATION_AVAILABLE | dataTypes.ITEM_FLAG.AVAILABLE)) > 0 &&
            (option.flag & dataTypes.ITEM_FLAG.IGNORE_ITEM) === 0 &&
            (option.supplier === dataTypes.SUPPLIER_OPTION || option.supplier === dataTypes.DEALER_OPTION) &&
            option.dataType === dataTypes.DATA_TYPES.FEATURE.DATA_TYPE_ID &&
            (option.typeID <= dataTypes.DATA_TYPES.FEATURE.TYPES.SAFETY ||
                option.typeID >= dataTypes.DATA_TYPES.FEATURE.TYPES.EXTERIOR_COLOUR) &&
            (!includes(configurationOptionsCodes, `${option.code}>${option.supplier}`) ||
                (option.flag & dataTypes.ITEM_FLAG.CONFIGURATION_SELECTED) > 0)
        );
    }).map((option) => {
        return OptionService.resetOptionFromProtectionItem(option);
    });

    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            OptionsPackageService.resetOptionsPackageAvailableOptions(
                optionsPackage,
                availableOptions,
                configurationOptions
            )
        )
    );
}

export function setSelectedOptions(modelYear, options) {
    if (!isArrayLikeObject(options)) {
        return modelYear;
    }

    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            OptionsPackageService.resetOptionsPackageOptions(optionsPackage, options)
        )
    );
}

function recalculatePayments(modelYear, paymentCalculationParameters) {
    return paymentCalculationParameters
        ? modelYear
              .set(
                  'years',
                  modelYear.years.map((year) => YearService.recalculateYearPayments(year, paymentCalculationParameters))
              )
              .set(
                  'optionsPackages',
                  modelYear.optionsPackages.map((optionsPackage) =>
                      OptionsPackageService.recalculateOptionsPackagePayments(
                          optionsPackage,
                          paymentCalculationParameters
                      )
                  )
              )
        : modelYear;
}

function recalculatePurchaseAmounts(modelYear, paymentCalculationParameters) {
    return paymentCalculationParameters
        ? modelYear
              .set(
                  'years',
                  modelYear.years.map((year) => YearService.recalculateYearPayments(year, paymentCalculationParameters))
              )
              .set(
                  'optionsPackages',
                  modelYear
                      .get('optionsPackages')
                      .map((optionsPackage) =>
                          OptionsPackageService.recalculateOptionsPackagePurchaseAmounts(
                              optionsPackage,
                              paymentCalculationParameters
                          )
                      )
              )
        : modelYear;
}

export function resetPaymentsAndPurchaseAmounts(modelYear, purchaseMethods) {
    const paymentCalculationParameters = purchaseMethods ? purchaseMethods.getPaymentCalculationParameters() : '';
    return recalculatePurchaseAmounts(modelYear, paymentCalculationParameters);
}

export function selectPurchaseMethod(modelYear, purchaseMethod) {
    return modelYear
        .set('years', modelYear.years.map((year) => YearService.resetYearPurchaseMethod(year, purchaseMethod)))
        .set(
            'optionsPackages',
            modelYear.optionsPackages.map((optionsPackage) =>
                OptionsPackageService.resetOptionsPackagePurchaseMethod(optionsPackage, purchaseMethod)
            )
        );
}

export function selectPaymentFrequency(modelYear, paymentFrequency) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithPaymentFrequency(paymentFrequency);
    return recalculatePayments(modelYear, paymentCalculationParameters);
}

export function selectTerm(modelYear, term) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithTerm(term);
    return recalculatePayments(modelYear, paymentCalculationParameters);
}

export function selectKMPlan(modelYear, kmPlan) {
    const purchaseMethods = modelYear.getSelectedOptionsPackagePurchaseMethods();
    if (!purchaseMethods) {
        return modelYear;
    }

    const paymentCalculationParameters = purchaseMethods.getPaymentCalculationParameters();
    const newOptionsPackages = modelYear.optionsPackages.map((optionsPackage) =>
        OptionsPackageService.selectOptionsPackageKMPlan(optionsPackage, kmPlan)
    );

    return modelYear.set(
        'optionsPackages',
        newOptionsPackages.map((optionsPackage) =>
            OptionsPackageService.recalculateOptionsPackagePayments(optionsPackage, paymentCalculationParameters)
        )
    );
}

export function setCashDown(modelYear, cashDown) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithCashDown(cashDown);
    return recalculatePayments(modelYear, paymentCalculationParameters);
}

export function setTradeIn(modelYear, tradeIn) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithTradeIn(tradeIn);
    return recalculatePurchaseAmounts(modelYear, paymentCalculationParameters);
}

export function setLien(modelYear, lien) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithLien(lien);
    return recalculatePayments(modelYear, paymentCalculationParameters);
}

export function setExteriorColourAmounts(modelYear, exteriorColourAmounts) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithExteriorColour(exteriorColourAmounts);
    return recalculatePurchaseAmounts(modelYear, paymentCalculationParameters);
}

export function setInteriorColourAmounts(modelYear, interiorColourAmounts) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithInteriorColour(interiorColourAmounts);
    return recalculatePurchaseAmounts(modelYear, paymentCalculationParameters);
}

export function setOptionsAmounts(modelYear, optionsAmounts) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithOptions(optionsAmounts);
    return recalculatePayments(modelYear, paymentCalculationParameters);
}

export function setTaxesIncluded(modelYear, taxesIncluded) {
    const paymentCalculationParameters = modelYear.getPaymentCalculationWithTaxesIncluded(taxesIncluded);
    return recalculatePayments(modelYear, paymentCalculationParameters);
}

export function resetModelYearFromQuote(modelYear, quote) {
    if (isEmpty(quote) || !isObjectLike(quote)) {
        return modelYear;
    }

    let newModelYear = modelYear;
    const cabSizeCode = quote.vehicle.cabSizeCode;
    if (cabSizeCode && modelYear.cabSizes && modelYear.cabSizes.length) {
        const selectedCabSize = find(modelYear.cabSizes, (cabSize) => {
            return cabSize.code === cabSizeCode;
        });
        if (!selectedCabSize) {
            return newModelYear;
        }
        newModelYear = setSelectedCabSize(newModelYear, selectedCabSize);

        const boxSizeCode = quote.vehicle.boxSizeCode;
        const boxSizeDesc = isEmpty(quote.vehicle.boxSize) ? '' : quote.vehicle.boxSize[0];
        if (boxSizeCode && modelYear.boxSizes && modelYear.boxSizes.length) {
            const selectedBoxSize = find(modelYear.boxSizes, (boxSize) => {
                return boxSize.code === boxSizeCode && boxSize.selectionCode === `${boxSizeCode}-${boxSizeDesc}`;
            });

            if (!selectedBoxSize) {
                return newModelYear;
            }
            newModelYear = setSelectedBoxSize(newModelYear, selectedBoxSize.set('enabled', true));
        }
    }
    const configurationId = `${quote.vehicle.modelTrimCode}|${quote.vehicle.configurationId}`;

    const selectedModelTrim = find(newModelYear.modelTrims.toArray(), (modelTrim) => {
        return !isUndefined(modelTrim.configurationIds[configurationId]);
    });

    if (!selectedModelTrim) {
        return newModelYear;
    }
    newModelYear = setSelectedModelTrim(newModelYear, selectedModelTrim);

    const selectedDrivePowerTrain = find(newModelYear.drivePowerTrains.toArray(), (drivePowerTrain) => {
        return !isUndefined(drivePowerTrain.configurationIds[configurationId]);
    });

    if (!selectedDrivePowerTrain) {
        return newModelYear;
    }
    newModelYear = setSelectedDrivePowerTrain(newModelYear, selectedDrivePowerTrain);

    const selectedOptionsPackage = find(newModelYear.optionsPackages.toArray(), (optionsPackage) => {
        return optionsPackage.configuration.selectionCode === configurationId;
    });

    if (!selectedOptionsPackage) {
        return newModelYear;
    }
    newModelYear = setSelectedOptionsPackage(newModelYear, selectedOptionsPackage, true);

    const selectedExteriorColourCode = quote.vehicle.purchaseMethods.getSelectedExteriorColourCode();
    if (selectedExteriorColourCode) {
        const selectedExteriorColour = find(
            selectedOptionsPackage.configuration.exteriorColours.toArray(),
            (colour) => {
                return colour.colourCode === selectedExteriorColourCode;
            }
        );
        if (selectedExteriorColour) {
            newModelYear = setSelectedExteriorColour(newModelYear, selectedExteriorColour);
        }
    }

    const selectedInteriorColourCode = quote.vehicle.purchaseMethods.getSelectedInteriorColourCode();
    if (selectedInteriorColourCode) {
        const selectedInteriorColour = find(
            selectedOptionsPackage.configuration.interiorColours.toArray(),
            (colour) => {
                return colour.colourCode === selectedInteriorColourCode;
            }
        );
        if (selectedInteriorColour) {
            newModelYear = setSelectedInteriorColour(newModelYear, selectedInteriorColour);
        }
    }

    const paymentCalculationParameters = quote.vehicle.purchaseMethods.getPaymentCalculationParameters();
    newModelYear = selectPurchaseMethod(newModelYear, quote.vehicle.purchaseMethods.getSelectedPurchaseMethod());
    newModelYear = recalculatePayments(newModelYear, paymentCalculationParameters);
    const selectedFinance = quote.vehicle.purchaseMethods.getSelectedFinance();
    const annualKMAllowed = selectedFinance ? selectedFinance.annualKMAllowed : 0;
    if (annualKMAllowed > 0) {
        return selectKMPlan(newModelYear, annualKMAllowed);
    }

    newModelYear = resetConfigurationToken(newModelYear, quote.vehicle.configurationToken);
    return newModelYear;
}

export function resetModelYearOptionsFromQuote(modelYear, quote) {
    if (isEmpty(quote) || !isObjectLike(quote)) {
        return modelYear;
    }

    let selectedOptionsPackage = modelYear.getSelectedOptionsPackage();
    if (!selectedOptionsPackage) {
        return modelYear;
    }

    const quoteOptions = [].concat(
        quote.vehicle.purchaseMethods.getSelectedOptionsAndColours(),
        quote.vehicle.purchaseMethods.getSelectedDealerFees()
    );
    const quoteSelectedOptions = filter(quoteOptions, (option) => {
        return option.selected;
    });
    const dealerSelectedOptions = filter(quoteSelectedOptions, (option) => {
        return option.isDealerInventoryOption() || option.isDealerFee();
    });
    const dealerSelectedDiscounts = quote.vehicle.purchaseMethods.getSelectedDealerDiscounts(true);

    if (dealerSelectedOptions.length || dealerSelectedDiscounts.length) {
        const configurationOptions = map(selectedOptionsPackage.configuration.options, (option) => {
            switch (option.id) {
                case DEALER_INVENTORY:
                    return option.set(
                        'itemsPackages',
                        unionBy(option.itemsPackages, dealerSelectedOptions, 'selectionCode')
                    );
                case DEALER_DISCOUNT:
                    return option.set(
                        'itemsPackages',
                        unionBy(option.itemsPackages, dealerSelectedDiscounts, 'selectionCode')
                    );
                default:
                    return option;
            }
        });

        selectedOptionsPackage = selectedOptionsPackage.setIn(['configuration', 'options'], configurationOptions);
    }
    const resetQuoteSelectedOptions = resetQuoteItemCategoriesFromOptionsValidation(
        selectedOptionsPackage.configuration.options,
        quoteSelectedOptions
    );

    let newModelYear = setSelectedOptions(modelYear, resetQuoteSelectedOptions);
    const paymentCalculationParameters = quote.vehicle.purchaseMethods.getPaymentCalculationParameters();
    newModelYear = selectPurchaseMethod(newModelYear, quote.vehicle.purchaseMethods.getSelectedPurchaseMethod());
    return recalculatePayments(newModelYear, paymentCalculationParameters);
}

export function setSelectedModelYear(modelYear, selectedYear) {
    const newModelYear = modelYear
        .set('year', selectedYear.year)
        .set('modelTrims', selectedYear.modelTrims)
        .set('drivePowerTrains', selectedYear.drivePowerTrains)
        .set('optionsPackages', selectedYear.optionsPackages)
        .set('years', modelYear.years.map((year) => year.set('selected', year.year === selectedYear.year)));

    if (!selectedYear.purchaseMethods.getSelectedPurchaseMethodIsValid()) {
        return selectPurchaseMethod(newModelYear, PurchaseMethodsConstants.CASH_PURCHASE);
    }
    return newModelYear;
}

export function resetEmployeeRebateAdjustment(modelYear, rebateAdjustment) {
    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            OptionsPackageService.resetOptionsPackageEmployeeRebateAdjustment(optionsPackage, rebateAdjustment)
        )
    );
}

export function resetPercentageRebateAdjustment(modelYear, rebateAdjustment) {
    return modelYear.set(
        'optionsPackages',
        modelYear.optionsPackages.map((optionsPackage) =>
            OptionsPackageService.resetOptionsPackagePercentageRebateAdjustment(optionsPackage, rebateAdjustment)
        )
    );
}

export default {
    resetModelYearFromOriginData,
    setSelectedDrivePowerTrain,
    setSelectedCabSize,
    setSelectedBoxSize,
    setSelectedModelTrim,
    setSelectedOptionsPackage,
    setSelectedExteriorColour,
    setSelectedInteriorColour,
    setSelectedModelYear,
    resetModelYears,
    resetModelYear,
    resetConfigurationToken,
    resetOptionsAvailability,
    resetOptionsSelectionResolutions,
    setAvailableOptions,
    setSelectedOptions,
    resetPaymentsAndPurchaseAmounts,
    selectPurchaseMethod,
    selectPaymentFrequency,
    selectTerm,
    selectKMPlan,
    setCashDown,
    setTradeIn,
    setLien,
    setExteriorColourAmounts,
    setInteriorColourAmounts,
    setOptionsAmounts,
    setTaxesIncluded,
    resetModelYearFromQuote,
    resetModelYearOptionsFromQuote,
    resetEmployeeRebateAdjustment,
    resetPercentageRebateAdjustment,
};



// WEBPACK FOOTER //
// ./src/ModelYear/services/ModelYearService/ModelYearService.js